import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  PLATFORM_ID,
  SimpleChanges,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  TextBlockTitleVariant,
  TextBlockAlign,
  TextBlockCheckListItemType,
} from '../../../shared/enums/text-block-type.enums';
import { CaasComponents } from '../../../shared/interfaces/caas/caas.interface';
import { CaasBodytextService } from '../../../services/caas-bodytext.service';
import { SafeHtml } from '@angular/platform-browser';
import { safeAssign } from '../../../shared/utils/safe-assign.util';
import { ErrorHandlerService } from '../../../services/error-handler.service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

@Component({
  selector: 'hdi-text-block',
  standalone: true,
  imports: [CommonModule, NgxSkeletonLoaderModule],
  templateUrl: './text-block.component.html',
  styleUrls: ['./text-block.component.scss'],
  host: { id: crypto.randomUUID().toString() },
})
export class TextBlockComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() caasData?: CaasComponents['value'][number];
  @Input() titleVariant?: TextBlockTitleVariant;
  @Input() titleContent?: string;
  @Input() titleAlign?: TextBlockAlign = TextBlockAlign.CENTER;
  @Input() paragraph?: string | SafeHtml;
  @Input() paragraphAlign?: TextBlockAlign = TextBlockAlign.CENTER;
  @Input() sectionBackgroundColor: 'white' | 'grey' = 'white';

  public readonly TextBlockTitleVariant = TextBlockTitleVariant;
  public readonly TextBlockAlign = TextBlockAlign;
  public readonly TextBlockCheckListItemType = TextBlockCheckListItemType;

  public isLoading = true;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly caasBodytextService: CaasBodytextService,
    private readonly errorHandler: ErrorHandlerService,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['caasData'] && this.caasData) {
      this.setCaasContent(this.caasData);
      if (isPlatformBrowser(this.platformId)) {
        this.isLoading = false;
      }
      this.cdr.detectChanges();
    }
    if (this.titleContent || this.paragraph) {
      if (isPlatformBrowser(this.platformId)) {
        this.isLoading = false;
      }
    }
  }

  ngAfterViewInit(): void {
    this.caasBodytextService.setRouterLinks();
  }

  ngOnDestroy(): void {
    this.caasBodytextService.removeRouterLinks();
  }

  setCaasContent(caasData: CaasComponents['value'][number]) {
    if (!caasData?.formData) {
      console.warn('TextBlockComponent: caasData or formData is missing');
      return;
    }
    let bodytext = '';
    safeAssign(
      () => {
        this.setCaasTitle(caasData);
      },
      'Assigning title',
      this.errorHandler
    );
    safeAssign(
      () => {
        bodytext =
          caasData.formData.st_content?.value ||
          caasData.formData.st_bodytext?.value ||
          '';
      },
      'Assigning bodytext',
      this.errorHandler
    );

    if (!bodytext) {
      console.warn('TextBlockComponent: bodytext is missing');
      return;
    }
    safeAssign(
      () => {
        this.paragraph =
          this.caasBodytextService.extractTextEditorContent(bodytext);
      },
      'Assigning paragraph',
      this.errorHandler
    );

    const checkListRegex = /<ul[\s\S]*?<\/ul>/gi;
    safeAssign(
      () => {
        if (caasData.formData.st_alignment?.value?.identifier === 'center') {
          this.paragraphAlign = checkListRegex.test(bodytext)
            ? TextBlockAlign.LEFT
            : TextBlockAlign.CENTER;
        } else {
          this.paragraphAlign = TextBlockAlign.LEFT;
        }
      },
      'Assigning paragraph alignment',
      this.errorHandler
    );
  }

  setCaasTitle(caasData: CaasComponents['value'][number]) {
    if (!caasData?.formData?.st_header?.value) {
      console.warn('TextBlockComponent: st_header is missing');
      this.titleContent = '';
      return;
    }
    safeAssign(
      () => {
        this.titleContent = caasData?.formData?.st_header?.value;
      },
      'Assigning title',
      this.errorHandler
    );

    this.titleVariant = TextBlockTitleVariant.H2;
    safeAssign(
      () => {
        this.titleAlign =
          caasData.formData.st_alignment?.value?.identifier === 'center'
            ? TextBlockAlign.CENTER
            : TextBlockAlign.LEFT;
      },
      'Assigning title align',
      this.errorHandler
    );
  }
}
